/**
 * Slovenian — `pageTour` namespace: per-page guided tours ("Vodnik po tej strani",
 * the ? button in the header). Keys: pageTour.<tourId>.<step>.{tag,title,body}
 * (see composables/pageTours.ts).
 */
export default {
    pageTour: {
        button: {
            label: 'Vodnik po tej strani',
        },
        controls: {
            next: 'Naprej',
            back: 'Nazaj',
            done: 'Končano',
            stepOf: 'Korak {current} od {total}',
        },

        dashboard: {
            aggregate: {
                tag: 'Pregled',
                title: 'Izpostavljenost skupine',
                body: 'Skupna neto pozicija vseh zaklenjenih (LOCKED) pogodb po 15-minutnih intervalih, zložena po državi (HR + SI). Nad ničlo = KRATKA (premalo nabavljeno), pod ničlo = DOLGA (presežek). Iz istih posnetkov pozicije CRM nadzorna plošča riše črto »terminske pogodbe — zgornja meja«.',
            },
            toolbar: {
                tag: 'Filtri grafa',
                title: 'Obdobje, država in delitev',
                body: 'Izberite OD/DO (privzeto: danes + 6 dni), zožite na HR ali SI in vklopite delitev napoved/futures. Os grafa prikazuje samo dneve s podatki, zato je lahko ožja od izbranega obdobja — rumena opomba vas na to opozori.',
            },
            refresh: {
                tag: 'Osveževanje',
                title: 'Naložite sveže posnetke',
                body: 'Združevanje pozicij se v ozadju zažene ob vsakem prehodu APPROVED ↔ LOCKED. Ko pogodbo zaklenete, kliknite Osveži, da graf prebere na novo zapisane posnetke.',
            },
            list: {
                tag: 'Seznam pogodb',
                title: 'Terminske pogodbe',
                body: 'Vsaka vrstica je ena futures pogodba: stran (BUY/SELL), vrsta produkta, država, status, nasprotna stranka, obdobje dobave, količina in cena. Klik na vrstico odpre povzetek na desni; puščici gor/dol in Enter delujejo s tipkovnice.',
            },
            filters: {
                tag: 'Filtri seznama',
                title: 'Stran, status, država',
                body: 'Seznam je privzeto filtriran na status APPROVED — osnutki (DRAFT) in zaklenjene (LOCKED) pogodbe niso vidni, dokler statusa ne preklopite na »Vse« ali drugega. Filtri se ohranijo, dokler ste v aplikaciji.',
            },
            newContract: {
                tag: 'Glavno dejanje',
                title: 'Nova pogodba',
                body: 'Odpre obrazec za novo pogodbo v desnem podoknu. Vidno samo operaterjem s pravico futures:write.',
            },
            viewPosition: {
                tag: 'Pozicija',
                title: 'Prikaži pozicijo',
                body: 'Odpre stran pozicije te pogodbe: koliko prispeva k skupni poziciji po dnevih, v primerjavi z napovedjo in ostalimi futures pogodbami.',
            },
            form: {
                tag: 'Desno podokno',
                title: 'Obrazec nove pogodbe',
                body: 'Ko ni izbrana nobena pogodba, tukaj vnesete novo: nasprotno stranko, državo, stran, produkt, obdobje dobave, MTU polnitev v MW in ceno. »Nadaljuj« ustvari pogodbo v statusu DRAFT in vas pelje v urejevalnik košar.',
            },
            summary: {
                tag: 'Desno podokno',
                title: 'Povzetek pogodbe',
                body: 'Pregled izbrane pogodbe z gumbi za spremembo statusa (Validiraj → Odobri → Zakleni). »Uredi pogodbo« odpre glavo, »Nova pogodba« vrne obrazec.',
            },
        },

        contractEditor: {
            header: {
                tag: 'Glava',
                title: 'Pogodba in status',
                body: 'Nasprotna stranka, stran (BUY/SELL), vrsta produkta, vir profila, status in različica; vrstica spodaj podaja obdobje dobave, skupno količino in ceno. »Uredi pogodbo« spremeni glavo (ne košar) — sprememba odobrene pogodbe jo samodejno vrne v VALIDATED.',
            },
            status: {
                tag: 'Potek statusa',
                title: 'Validiraj, odobri, zakleni',
                body: 'DRAFT → VALIDATED → APPROVED → LOCKED; preklic je dokončen. Odobri, Zakleni in Odkleni zahtevajo pravico futures:approve. Šele zaklenjena (LOCKED) pogodba vstopi v združevanje pozicij — in s tem v izpostavljenost skupine in zgornjo mejo v CRM-u.',
            },
            dayNav: {
                tag: 'Dnevi dobave',
                title: 'Navigacija po dnevih',
                body: 'Košare urejate dan za dnem. Puščici ali Alt+← / Alt+→ zamenjata dan; vrstica spodaj pove, kateri dan po vrsti je in koliko intervalov ima — 96 običajno, 92 na dan prehoda na poletni čas, 100 na dan vrnitve.',
            },
            grid: {
                tag: 'Košare',
                title: 'MW na 15-minutni interval',
                body: 'Štiri vrstice so četrtine dneva (00–06, 06–12, 12–18, 18–24 po lokalnem času); konično obdobje 08–20 je označeno. Vsaka celica je moč v MW za ta interval (MTU), ne energija v MWh. Vnos se zapomni takoj, shrani pa se šele s »Potrdi in shrani«.',
            },
            toolbar: {
                tag: 'Orodja dneva',
                title: 'Kopiraj in ponastavi dan',
                body: 'Kopiraj prenese profil tega dne na izbrane druge dneve; Ponastavi postavi vse košare dneva na nič. Oboje je onemogočeno pri zaklenjeni pogodbi.',
            },
            liveChart: {
                tag: 'Graf',
                title: 'Profil dneva v živo',
                body: 'Riše trenutni dan, medtem ko tipkate — hitra kontrola, ali je PEAK zapolnjen samo 08–20, BASE pa ravno čez ves dan.',
            },
            footer: {
                tag: 'Shranjevanje',
                title: 'Skupaj in Potrdi in shrani',
                body: 'Levo: vsota vseh košar, število dni in koliko jih je neshranjenih. »Potrdi in shrani« (ali Ctrl+S) odpre pregled po dnevih in pošlje novo različico; »Zavrzi« vrne na nadzorno ploščo. Zaklenjena pogodba je samo za branje.',
            },
            audit: {
                tag: 'Revizija',
                title: 'Zgodovina sprememb',
                body: 'Vsako ustvarjanje, sprememba in prehod statusa s časom, izvajalcem in razlikami prej/potem. Zavihek se odpre tudi neposredno s povezavo, ki se konča na #audit.',
            },
        },

        contractEditHeader: {
            form: {
                tag: 'Glava pogodbe',
                title: 'Urejanje glave',
                body: 'Tukaj spreminjate podatke pogodbe, ne košar — za profil po intervalih se vrnite v urejevalnik. Shranjevanje ustvari novo različico; če je nekdo vmes shranil svojo, strežnik vašo zavrne.',
            },
            counterparty: {
                tag: 'Identifikacija',
                title: 'Nasprotna stranka in reference',
                body: 'Naziv nasprotne stranke je obvezen; EIC in zunanji ID posla sta neobvezna in služita povezovanju z dokumenti in sistemi nasprotne stranke.',
            },
            country: {
                tag: 'Država',
                title: 'HR ali SI',
                body: 'Država določa tržni koledar (konične ure, prazniki) in knjigo pozicij, v katero pogodba vstopi. Po ustvarjanju jo lahko spremeni samo operater s pravico futures:approve — sprememba znova razreši profil.',
            },
            sideProduct: {
                tag: 'Stran in produkt',
                title: 'BUY/SELL in vrsta produkta',
                body: 'BUY zmanjša neto pozicijo (nabava), SELL jo poveča. BASE zapolni vse intervale, PEAK samo 08–20, OFF_PEAK samo zunaj tega, CUSTOM pa pusti košare za ročni vnos v urejevalniku.',
            },
            delivery: {
                tag: 'Obdobje dobave',
                title: 'OD / DO in hitra izbira',
                body: 'Gumbi (dan, teden, mesec, četrtletje, leto) nastavijo obdobje okoli začetnega datuma. Pri pogodbi MANUAL_BILATERAL je obdobje zaklenjeno, ker so košare vezane na to okno — za drugo obdobje ustvarite novo pogodbo.',
            },
            profileSource: {
                tag: 'Vir profila',
                title: 'EPEX_DEFAULT ali MANUAL_BILATERAL',
                body: 'EPEX_DEFAULT sam izračuna košare iz vrste produkta in MTU polnitve; MANUAL_BILATERAL pričakuje ročno nastavljene košare v urejevalniku.',
            },
            volume: {
                tag: 'Količina in cena',
                title: 'MW, ne MWh',
                body: 'MTU polnitev je moč v MW, ki gre v vsak aktivni 15-minutni interval; skupna količina se izračuna kot polnitev × število aktivnih intervalov. Cena je v €/MW, valuta vedno EUR.',
            },
            submit: {
                tag: 'Glavno dejanje',
                title: 'Shrani spremembe',
                body: 'Pošlje spremembo glave in vrne v urejevalnik košar; Prekliči vrne brez shranjevanja. Če se obdobje prekriva z obstoječo pogodbo, strežnik shranjevanje zavrne in zgoraj prikaže povezave na sporne pogodbe.',
            },
        },

        position: {
            header: {
                tag: 'Glava',
                title: 'Pozicija ene pogodbe',
                body: 'Stran je samo za branje: prikazuje, koliko ta pogodba prispeva k skupni poziciji po dnevih. Vrstica spodaj pove, kdaj je bilo zadnje združevanje zagnano ter koliko dni in intervalov pokriva.',
            },
            noSnapshot: {
                tag: 'Ni posnetka',
                title: 'Združevanje še ni bilo zagnano',
                body: 'Posnetki pozicije nastanejo šele, ko je pogodba zaklenjena (LOCKED) in združevanje opravljeno. Dokler jih ni, tudi grafa ni.',
            },
            dayNav: {
                tag: 'Izbira dneva',
                title: 'Dan za dnem',
                body: 'Spustni seznam ponuja vsak dan obdobja dobave; puščici ali Alt+← / Alt+→ preskočita na sosednji dan.',
            },
            toggles: {
                tag: 'Plasti',
                title: 'Kaj prikazati',
                body: 'Vklopite ali izklopite osnovno napoved, to pogodbo in ostale futures pogodbe. Oznaka DOLGA/KRATKA je smer po večini intervalov tega dne.',
            },
            chart: {
                tag: 'Graf',
                title: 'Zložena pozicija',
                body: 'Plasti: napoved, prispevek te pogodbe in preostanek ostalih futures pogodb; črta je končna pozicija. SELL pogodba dodaja, BUY odšteva. Če napoved še ni obdelana, je plast napovedi nič.',
            },
            summary: {
                tag: 'Povzetek dneva',
                title: 'Številke za izbrani dan',
                body: 'Vsota napovedi, prispevka te pogodbe in končne pozicije za dan, skupaj s smerjo. Vrednosti so vsote po intervalih.',
            },
            reference: {
                tag: 'Referenca',
                title: 'Oblike produktov',
                body: 'Statične ilustracije, kako izgledajo profili BASE, PEAK in OFF_PEAK ter kako se seštejejo v kombinirano futures pozicijo. To niso podatki te pogodbe — pravi seštevek po vseh pogodbah je na nadzorni plošči.',
            },
        },
    },
}
